import React, { useState } from "react";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function DeleteAccountDialog({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  const matches =
    user?.email && confirmEmail.trim().toLowerCase() === user.email.toLowerCase();

  const doDelete = async () => {
    if (!matches) return;
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      toast.success("Your account has been deleted.");
      onOpenChange?.(false);
      logout?.();
      window.location.href = "/";
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not delete account. Try again."
      );
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-rose-500/40 text-slate-100 max-w-md rounded-sm"
        data-testid="delete-account-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-rose-400 mb-1 flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" />
            Danger zone
          </div>
          <DialogTitle className="font-display text-xl">
            Delete your account
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            This permanently removes your account, projects, credit wallet, and
            unpaid orders. Paid payment records are kept for financial audit.
            This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-2">
          <Label className="text-xs text-slate-400">
            Type <span className="text-slate-200 font-mono">{user?.email}</span>{" "}
            to confirm
          </Label>
          <Input
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={user?.email}
            className="bg-slate-900 border-slate-800 rounded-sm text-slate-100"
            data-testid="delete-account-confirm-email"
          />
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={deleting}
            className="bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
            data-testid="delete-account-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={doDelete}
            disabled={!matches || deleting}
            data-testid="delete-account-confirm"
            className="bg-rose-500 hover:bg-rose-400 text-slate-950 font-semibold rounded-sm disabled:opacity-40"
          >
            {deleting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete forever
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
